import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import fetch from 'node-fetch'
import { brand } from '../../../redux/app-selectors'
import BusinessFinder from '../sme-form/business-finder'
import { Field, Label, Radio, RadioGroup, Input, Button } from '../../../atoms'
import { FsCore } from 'types/fs-core'
import SoleTraderDetails from './sole-trader-details'
import DirectorFinder from './director-details'
import { useForm } from 'react-hook-form'
import { shortuuid } from '../../../utils/guid'
import CashPlusCheckout from '../results-card/results-card'

interface SMEproviderFormProps {
  brand: 'money' | 'uswitch' | 'confused'
  sys: {
    id: string
  }
  name: string
}

export interface DirectorDetails {
  name?: string
  title?: string
  firstName?: string
  lastName?: string
  dateOfBirth?: string
  dateOfBirthFields?: {
    day?: string
    month?: string
    year?: string
  }
  emailAddress?: string
  mobileNumber?: string
  workNumber?: string
}

// This form replicates this cashplus journey:
// https://application.cashplus.com/forms/businessapplication/businessFormStep1/CPCorpDefault
const SMEproviderForm: React.FC<SMEproviderFormProps> = ({ brand }) => {
  const [businessDirectors, setBusinessDirectors] = useState<DirectorDetails[]>([])
  const [termsAndConditionsURL, setTermsAndConditionsURL] = useState<string | null>()
  const [exisitingApplication, setExistingApplication] = useState<any | null>()
  const [loading, setLoading] = useState<boolean>(true)
  const { __FS_API_URL__ } = global
  const apiPath: string = __FS_API_URL__ || 'https://www.money.co.uk/api'

  const fetchTerms = async () => {
    try {
      const response = await fetch(`${apiPath}/cashplus/terms-and-conditions/0`)
      const data = await response.json()
      setTermsAndConditionsURL(data.Url)
    } catch (e) {}
  }

  const fetchApplication = async () => {
    try {
      const response = await fetch(
        `${apiPath}/cashplus/application?sessionId=${sessionStorage.getItem(
          'cashPlusSession',
        )}&brand=money`,
      )
      setLoading(false)
      const data = await response.json()
      if (response.status === 200 || response.status === 202) {
        setExistingApplication(data)
      }
    } catch (e) {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTerms()
    if (typeof window !== 'undefined') {
      const exisitingCashPlusSession = !!sessionStorage.getItem('cashPlusSession')
      if (exisitingCashPlusSession) {
        fetchApplication()
      } else {
        setLoading(false)
        sessionStorage.setItem('cashPlusSession', shortuuid())
      }
    }
  }, [])

  const {
    register,
    handleSubmit,
    errors,
    setValue,
    watch,
  }: { register: (name: string, validation: any) => any } = useForm({
    mode: 'all',
    defaultValues: {
      businessDetails: {
        businessType: 'soleTrader',
      },
      directorDetails: {
        title: undefined,
        firstName: undefined,
        lastName: undefined,
        dateOfBirth: undefined,
        emailAddress: undefined,
        mobileNumber: undefined,
        nationality: undefined,
      },
      otherDetails: {
        businessNameOnCard: undefined,
        fscsAcknowledged: undefined,
      },
    },
  })

  const companyName = watch('businessDetails.companyName')
  const companyNumber = watch('businessDetails.companyNumber')
  const businessType = watch('businessDetails.businessType')

  useEffect(() => {
    const fetchDirectorData = async () => {
      const res = await fetch(`/api/company/${companyNumber}/officers`)
      const json = await res.json()
      setBusinessDirectors(json.data)
    }
    if (companyNumber && businessType !== 'soleTrader') {
      fetchDirectorData()
    }
  }, [companyNumber])

  useEffect(() => {
    if (companyName) {
      setValue('otherDetails.businessNameOnCard', companyName)
    }
  }, [companyName])

  return (
    <div className='SMEform'>
      <h3>Apply for a Cashplus current account</h3>

      {exisitingApplication ? (
        <CashPlusCheckout
          status={exisitingApplication.status}
          reference_number={exisitingApplication.reference_number}
        />
      ) : loading ? (
        <div className='SMEform__loadingSkeleton' />
      ) : (
        <form>
          <Field className='SMEform__typeOfBusiness'>
            <Label>Type of business</Label>
            <div className='SMEform__input'>
              <RadioGroup column={true}>
                {[
                  { name: 'limitedCompany', text: 'Limited Company' },
                  { name: 'soleTrader', text: 'Sole Trader' },
                  { name: 'other', text: 'Other (e.g. Partnership)' },
                ].map(({ name, text }, index) => {
                  return (
                    <Radio
                      name={name}
                      value={name}
                      currentValue={watch('businessDetails.businessType') ?? ''}
                      onChange={(e) => {
                        setValue('businessDetails.businessType', e.target.value)
                      }}
                      key={index}
                      {...register('businessDetails.businessType', {
                        required: 'Please enter your type of business',
                      })}
                    >
                      {text}
                    </Radio>
                  )
                })}
              </RadioGroup>
            </div>
          </Field>

          {businessType !== 'other' && (
            <BusinessFinder
              businessDetails={watch('businessDetails')}
              brand={brand}
              register={register}
              setValue={setValue}
              errors={errors}
              soleTrader={businessType === 'soleTrader'}
            >
              <Field>
                <Label>Number of employees</Label>
                <Input
                  name='numberOfEmployees'
                  type='number'
                  value={watch('businessDetails.numberOfEmployees') ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setValue('businessDetails.numberOfEmployees', e.target.value)
                  }}
                  {...register('businessDetails.numberOfEmployees', {
                    required: 'Please enter the Number of employees in your business',
                  })}
                />
                {errors?.businessDetails?.numberOfEmployees && (
                  <div className='SMEform__error'>
                    {errors.businessDetails.numberOfEmployees.message}
                  </div>
                )}
              </Field>
              <Field>
                <Label>Business name as it should appear on card</Label>
                <Input
                  value={watch('otherDetails.businessNameOnCard') ?? companyName ?? ''}
                  name='businessNameOnCard'
                  type='text'
                  onChange={(e) => {
                    setValue('otherDetails.businessNameOnCard', e.target.value)
                  }}
                  {...register('otherDetails.businessNameOnCard', {
                    required: 'Please enter the name you would like on your bank card',
                  })}
                />
                {errors?.otherDetails?.businessNameOnCard && (
                  <div className='SMEform__error'>
                    {errors.otherDetails.businessNameOnCard.message}
                  </div>
                )}
              </Field>
            </BusinessFinder>
          )}

          {businessType === 'soleTrader' ? (
            <SoleTraderDetails
              directorDetails={watch('directorDetails', {})}
              register={register}
              setValue={setValue}
              errors={errors}
            />
          ) : companyName && businessType !== 'other' ? (
            <DirectorFinder
              directorDetails={watch('directorDetails', {})}
              businessDirectors={businessDirectors}
              register={register}
              setValue={setValue}
              errors={errors}
            />
          ) : (
            <></>
          )}
          {companyName && businessType !== 'other' && (
            <Field className='SMEform__typeOfBusiness'>
              <Label>Review Terms</Label>
              <div className='SMEform__input'>
                <div className='SMEform__fscsConsent'>
                  <Input
                    type='checkbox'
                    name='marketingConsent'
                    value='fscsConsent'
                    {...register('otherDetails.fscsConsent', {
                      required: 'Please review and aknowledge the terms',
                    })}
                    onChange={(e) => {
                      setValue('otherDetails.fscsConsent', e.target.value)
                    }}
                  />
                  <div>
                    I acknowledge the cover of the{' '}
                    <a href='https://www.cashplus.com/fscs' target='blank'>
                      Financial Services Compensation Scheme
                    </a>
                  </div>
                </div>
                {termsAndConditionsURL && businessType !== 'other' && (
                  <div className='SMEform__fscsConsent'>
                    <Input
                      type='checkbox'
                      name='productConsent'
                      value='productConsent'
                      {...register('otherDetails.productConsent', {
                        required: 'Please review and accept the product terms',
                      })}
                      onChange={(e) => {
                        setValue('otherDetails.productConsent', e.target.value)
                      }}
                    />
                    <div>
                      I have read and accepted the current{' '}
                      <a href={termsAndConditionsURL} target='blank'>
                        Terms and Conditions
                      </a>{' '}
                      and agree to apply for a Cashplus Business Bank Account
                    </div>
                  </div>
                )}
                {businessType !== 'other' && (
                  <div className='SMEform__fscsConsent'>
                    <p>
                      Only a director, or persons with permission of a director, can submit this
                      application. By clicking ‘I agree to apply for a Cashplus Business Bank
                      Account’ you agree to the above Terms and Conditions. As part of the
                      application, APS will need to verify your identity which will involve APS
                      checking your personal details against third party databases.
                    </p>
                  </div>
                )}

                {businessType !== 'other' && (
                  <div className='SMEform__fscsConsent'>
                    <p>
                      I consent to my details and those of my company to be passed to the partners
                      for the purpose of opening and providing the services, and to being contacted
                      directly by the partners for the purposes of fulfilling the request for those
                      services.
                    </p>
                  </div>
                )}
                {(errors?.otherDetails?.productConsent || errors?.otherDetails?.fscsConsent) && (
                  <div className='SMEform__error'>Please tick all the checkboxes above</div>
                )}
              </div>
            </Field>
          )}

          {/* eslint-disable-next-line no-console */}
          {businessType !== 'other' && (
            <Button
              variant='primary'
              size='lg'
              onClick={handleSubmit(async (data) => {
                const { businessDetails, directorDetails, otherDetails } = data
                const finalBody = {
                  businessType: businessDetails.businessType,
                  businessName: businessDetails.companyName,
                  numberOfEmployees: businessDetails.numberOfEmployees,
                  businessNameOnCard: otherDetails.businessNameOnCard,
                  productId: 0,
                  tCsReferenceNumber: 123,
                  title: directorDetails.title,
                  firstName: directorDetails.firstName,
                  lastName: directorDetails.lastName,
                  dateOfBirthYear: directorDetails.dateOfBirthFields.year,
                  dateOfBirthMonth: directorDetails.dateOfBirthFields.month,
                  dateOfBirthDay: directorDetails.dateOfBirthFields.day,
                  emailAddress: directorDetails.emailAddress,
                  mobileNumber: directorDetails.mobileNumber,
                  workNumber: directorDetails.mobileNumber,
                  homeAddress: {
                    postcode: businessDetails.postal_code,
                    addressLine: `${businessDetails.address_line_1} ${
                      businessDetails.address_line_2 ?? ''
                    }`,
                    townOrCity: businessDetails.locality,
                  },
                  nationality: directorDetails.nationality,
                  companyRegistrationNumber: businessDetails.companyNumber,
                  registeredBusinessAddress: {
                    postcode: businessDetails.postal_code,
                    addressLine: `${businessDetails.address_line_1} ${
                      businessDetails.address_line_2 ?? ''
                    }`,
                    townOrCity: businessDetails.locality,
                  },
                  fscsAcknowledged: true,
                }
                const res = await fetch(
                  `${apiPath}/cashplus/application?sessionId=${sessionStorage.getItem(
                    'cashPlusSession',
                  )}&brand=money&requestId=09876`,
                  {
                    method: 'POST',
                    body: JSON.stringify(finalBody),
                    headers: {
                      'Content-Type': 'application/json',
                    },
                  },
                )
                if (res.ok) {
                  window.location.href = '/business/cashplus/results'
                }
                const response = await res.json()
                // eslint-disable-next-line no-console
                console.log(response)
              })}
            >
              Submit
            </Button>
          )}
        </form>
      )}
      {businessType === 'other' && (
        <div className='CashPlusForm__notSupported'>
          <div className='CashPlusForm__notSupported__text'>
            Unfortunately our current journey does not support businesses other than Sole Traders or
            limited companies
          </div>
          <div className='CashPlusForm__notSupported__text'>
            Click the button below to apply via the cashPlus website
          </div>
          <Button variant='primary' size='lg' href='/cashplussite'>
            Go to CashPlus
          </Button>
        </div>
      )}
    </div>
  )
}

export default connect((state: FsCore.State, props: any) => {
  return {
    brand: brand(state),
    ...props,
  }
})(SMEproviderForm)
